import { useState } from "react";
import {
  BarChart3,
  CalendarCheck,
  Check,
  FlaskConical,
  KeyRound,
  LayoutDashboard,
  LogOut,
  RotateCcw,
  Search,
  Settings,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { CapacityEditor } from "../components/CapacityEditor";
import { AVAIL_OPTIONS } from "../lib/capacity";

const createDemoClinician = () => ({
  id: "simplyrefer-demo-psychiatrist",
  first_name: "SimplyRefer Demo Psychiatrist",
  last_name: "",
  profession: "Psychiatrist",
  verification_status: "VERIFIED",
  is_verified: true,
  is_sample: true,
  availability_status: "TAKING_PATIENTS",
  assessment_291_status: "NOT_OFFERED",
  assessment_291_available_from: null,
  capacity_count: null,
  capacity_timing_type: null,
  capacity_from_date: null,
  capacity_last_confirmed_at: null,
  capacity_expires_at: null,
  psychiatry_referral_type: "",
  psychiatry_referral_type_other: "",
});

const NAV_ITEMS = [
  { label: "Overview", Icon: LayoutDashboard },
  { label: "Availability", Icon: CalendarCheck, available: true },
  { label: "Find clinician", Icon: Search },
  { label: "Referrals", Icon: KeyRound },
  { label: "Insights", Icon: BarChart3 },
  { label: "Settings", Icon: Settings },
];

const availabilityCardClass = (active) =>
  "relative flex flex-col rounded-md border p-4 text-left transition-all duration-200 " +
  (active
    ? "ring-2 ring-primary/30"
    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm");

export default function PsychiatristAvailabilityDemo() {
  const [clinician, setClinician] = useState(createDemoClinician);
  const [demoKey, setDemoKey] = useState(0);
  const [lastAction, setLastAction] = useState("Ready to explore");

  const updateAvailability = (value) => {
    setClinician((current) => ({ ...current, availability_status: value }));
    setLastAction("General availability changed in this demo only");
  };

  const toggle291 = () => {
    setClinician((current) => {
      const on291 = current.assessment_291_status && current.assessment_291_status !== "NOT_OFFERED";
      return {
        ...current,
        assessment_291_status: on291 ? "NOT_OFFERED" : "AVAILABLE_NOW",
        assessment_291_available_from: on291 ? null : current.assessment_291_available_from,
      };
    });
    setLastAction("291 assessment availability changed in this demo only");
  };

  const update291Date = (dateValue) => {
    setClinician((current) => ({
      ...current,
      assessment_291_status: dateValue ? "AVAILABLE_FROM" : "AVAILABLE_NOW",
      assessment_291_available_from: dateValue || null,
    }));
    setLastAction("291 assessment date changed in this demo only");
  };

  const resetDemo = () => {
    setClinician(createDemoClinician());
    setDemoKey((value) => value + 1);
    setLastAction("Demo reset to its starting values");
  };

  const onCapacityUpdated = (updated) => {
    setClinician((current) => ({ ...current, ...updated }));
    setLastAction("Patient numbers and dates changed in this demo only");
  };

  const on291 = clinician.assessment_291_status && clinician.assessment_291_status !== "NOT_OFFERED";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="psychiatrist-availability-demo">
      <div className="sticky top-0 z-30 border-b border-amber-300 bg-amber-50">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex items-start gap-2 text-amber-950">
            <FlaskConical className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="text-sm font-bold uppercase tracking-wide">Demo / test environment</p>
              <p className="text-sm">Try the availability controls below. Changes stay in this browser and are not saved or sent anywhere.</p>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={resetDemo} className="shrink-0 border-amber-300 bg-white" data-testid="demo-reset">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset demo
          </Button>
        </div>
      </div>

      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-2.5">
            <img src="/appicon.png" alt="" className="h-9 w-9 rounded-lg" />
            <span className="font-heading text-lg font-semibold tracking-tight text-slate-900">SimplyRefer</span>
          </div>
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            <span className="max-w-[150px] truncate text-sm font-medium text-slate-700 sm:max-w-none" data-testid="demo-account-name">
              SimplyRefer Demo Psychiatrist
            </span>
            <Button type="button" variant="outline" size="sm" disabled title="Disabled in the demo environment">
              <LogOut className="mr-2 h-4 w-4" /> <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto px-4 pb-3 sm:px-6 lg:px-8" aria-label="Psychiatrist portal navigation">
          {NAV_ITEMS.map(({ label, Icon, available }) => (
            <button
              type="button"
              key={label}
              disabled={!available}
              aria-disabled={!available}
              aria-current={available ? "page" : undefined}
              title={available ? "Availability demo" : "Unavailable in this demo"}
              className={
                "inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium " +
                (available
                  ? "bg-secondary text-secondary-foreground"
                  : "cursor-not-allowed text-slate-400 opacity-60")
              }
              data-testid={"demo-nav-" + label.toLowerCase().replace(/\s/g, "-")}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
        <div className="mb-5 flex flex-col gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
            <div>
              <p className="font-medium text-emerald-950">Safe demo mode</p>
              <p className="text-sm text-emerald-800">Nothing here is saved, billed, or sent to anyone - this is just for you to explore the features risk-free.</p>
            </div>
          </div>
          <span className="text-xs font-medium text-emerald-800" aria-live="polite" data-testid="demo-last-action">{lastAction}</span>
        </div>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6" data-testid="clinician-availability-section">
          <div>
            <h1 className="font-heading text-xl font-semibold text-slate-900 sm:text-2xl">Availability</h1>
            <p className="mt-1 text-sm text-slate-500">Let referring GPs know if you’re accepting patients.</p>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {AVAIL_OPTIONS.map((option) => {
              const active = clinician.availability_status === option.value;
              return (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => updateAvailability(option.value)}
                  data-testid={"availability-set-" + option.value}
                  style={active ? option.style : undefined}
                  className={availabilityCardClass(active)}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-slate-900">{option.label}</span>
                    {active ? (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground" data-testid={"availability-active-" + option.value}>
                        <Check className="h-3 w-3" />
                      </span>
                    ) : null}
                  </div>
                  <span className="mt-1 text-xs text-slate-500">{option.desc}</span>
                </button>
              );
            })}

            <button
              type="button"
              onClick={toggle291}
              data-testid="availability-toggle-291"
              style={on291 ? { backgroundColor: "#FFFBEB", borderColor: "#FCD34D" } : undefined}
              className={
                "relative flex flex-col rounded-md border p-4 text-left transition-all duration-200 " +
                (on291 ? "" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm")
              }
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-slate-900">291 assessments <span className="font-normal text-slate-400">(optional)</span></span>
                {on291 ? (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: "#92400E" }} data-testid="availability-active-291">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                ) : null}
              </div>
              <span className="mt-1 text-xs text-slate-500">Works alongside whichever status you pick above</span>
            </button>
          </div>

          {on291 ? (
            <div className="mt-3 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm md:ml-[calc(66.666667%+0.5rem)] md:w-[calc(33.333333%-0.5rem)]" data-testid="assessment-291-date-field">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="demo-291-date" className="text-sm font-semibold text-slate-900">When can you start offering 291 assessments?</label>
                <span className="shrink-0 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800">Optional</span>
              </div>
              <p className="mt-1 text-xs leading-5 text-slate-500">Leave blank if you are available now. This date only applies to 291 assessments.</p>
              <input
                id="demo-291-date"
                type="date"
                className="mt-3 w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 shadow-sm outline-none transition hover:border-amber-300 focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
                data-testid="assessment-291-date"
                value={clinician.assessment_291_available_from || ""}
                onChange={(event) => update291Date(event.target.value)}
              />
            </div>
          ) : null}

          <CapacityEditor
            key={demoKey}
            clinician={clinician}
            onUpdated={onCapacityUpdated}
            locked={false}
            demoMode
          />
        </section>

        <p className="mt-4 text-center text-xs text-slate-500">This is a test account. Refreshing or resetting the page clears any changes.</p>
      </main>
    </div>
  );
}
